// KPI tiles + the metric picker that drives them.
//
// Every metric in the catalogue is addable as a tile, a table column AND a trend
// series — the picker below is shared by all three surfaces, so "Search IS" means
// the same thing and formats the same way wherever it appears.

import { useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useClickOutside } from '../../lib/useClickOutside';
import { cn } from '../../lib/utils';
import {
  GOOGLE_ADS_METRICS,
  metricDef,
} from '../../lib/googleAdsMetrics';
import { NOT_MEASURED, formatMetric, isMetricUnmeasured } from './metricFormat';
import { PANEL_TITLE_CLASS } from '../reporting/PanelTitle';
import type { GoogleAdsAttributeDef, GoogleAdsAttributeKey } from './attributeColumns';
import type { GoogleAdsMetricKey, GoogleAdsMetrics } from '../../types/googleAdsReporting';

// ---------------------------------------------------------------------------
// Metric picker
// ---------------------------------------------------------------------------

/** One checkable row — shared by the metric and attribute groups. */
function PickerRow({
  label,
  hint,
  checked,
  disabled,
  title,
  onClick,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  disabled?: boolean;
  title?: string;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={title}
      className={cn(
        'flex w-full items-start gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent',
        disabled && 'cursor-not-allowed opacity-40',
      )}
    >
      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
        {checked && <Check className="h-3.5 w-3.5" />}
      </span>
      <span className="min-w-0">
        <span className="block">{label}</span>
        {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
      </span>
    </button>
  );
}

export function MetricPicker({
  label,
  selected,
  onChange,
  max,
  attributes,
  selectedAttributes,
  onAttributesChange,
}: {
  label: string;
  selected: GoogleAdsMetricKey[];
  onChange: (next: GoogleAdsMetricKey[]) => void;
  /** Optional cap (KPI tiles get unreadable past ~8). */
  max?: number;
  /**
   * Optional SECOND group — the tree's entity-attribute columns (campaign type,
   * budget, ad strength…). Only the table's "Columns" picker passes these; the
   * KPI and Series pickers stay metric-only.
   *
   * ⚠️ Attributes have NO minimum and no cap: all-off is the default table, so
   * the "never remove the last one" rule that protects the metric list would be
   * wrong here.
   */
  attributes?: GoogleAdsAttributeDef[];
  selectedAttributes?: GoogleAdsAttributeKey[];
  onAttributesChange?: (next: GoogleAdsAttributeKey[]) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside([ref], () => setOpen(false), open);

  const toggle = (key: GoogleAdsMetricKey): void => {
    const has = selected.includes(key);
    if (has) {
      // Never let the last metric be removed — an empty tile row / chart reads
      // as broken rather than as a choice.
      if (selected.length === 1) return;
      onChange(selected.filter((k) => k !== key));
      return;
    }
    if (max && selected.length >= max) return;
    onChange([...selected, key]);
  };

  const attrSelection = selectedAttributes ?? [];
  const toggleAttribute = (key: GoogleAdsAttributeKey): void => {
    if (!onAttributesChange) return;
    onAttributesChange(
      attrSelection.includes(key)
        ? attrSelection.filter((k) => k !== key)
        : [...attrSelection, key],
    );
  };

  const atCap = max != null && selected.length >= max;
  const showGroups = Boolean(attributes && attributes.length > 0);
  // The button counts every column the table will draw, not just the metrics.
  const total = selected.length + (showGroups ? attrSelection.length : 0);

  return (
    <div className="relative" ref={ref}>
      <Button variant="outline" size="sm" onClick={() => setOpen((o) => !o)}>
        {label} ({total}) <ChevronDown className="ml-1 h-3.5 w-3.5" />
      </Button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 max-h-96 w-72 overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-md scroll-subtle">
          {showGroups && (
            <div className="px-2 pb-1 pt-1.5 text-xs font-medium text-muted-foreground">
              Metrics
            </div>
          )}
          {GOOGLE_ADS_METRICS.map((m) => {
            const isSelected = selected.includes(m.key);
            const disabled = !isSelected && atCap;
            return (
              <PickerRow
                key={m.key}
                label={m.label}
                hint={m.hint}
                checked={isSelected}
                disabled={disabled}
                title={disabled ? `Up to ${max} at a time` : m.hint}
                onClick={() => toggle(m.key)}
              />
            );
          })}
          {showGroups && (
            <>
              <div className="mt-1 border-t border-border px-2 pb-1 pt-2 text-xs font-medium text-muted-foreground">
                Campaign details
                <span className="ml-1 font-normal">— off by default</span>
              </div>
              {attributes!.map((a) => (
                <PickerRow
                  key={a.key}
                  label={a.label}
                  hint={a.hint}
                  checked={attrSelection.includes(a.key)}
                  title={a.hint}
                  onClick={() => toggleAttribute(a.key)}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI tiles
// ---------------------------------------------------------------------------

export function GoogleAdsKpis({
  metrics,
  selected,
  loading,
}: {
  metrics: GoogleAdsMetrics | undefined;
  selected: GoogleAdsMetricKey[];
  loading: boolean;
}): React.JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {selected.map((key) => {
        const def = metricDef(key);
        // An unmeasured metric is shown MUTED with an em dash and a reason, never
        // as 0 — see metricFormat for why that distinction matters commercially.
        const unmeasured = metrics ? isMetricUnmeasured(metrics, key) : false;
        return (
          <div
            key={key}
            className="rounded-lg border border-border bg-card p-4"
            title={def?.hint}
          >
            {/* Same title treatment as the canvas tiles — see PanelTitle. */}
            <div className={PANEL_TITLE_CLASS}>{def?.label ?? key}</div>
            <div
              className={cn(
                'mt-1 text-2xl font-semibold tabular-nums',
                unmeasured && 'text-muted-foreground',
              )}
            >
              {loading && !metrics ? (
                <span className="inline-block h-7 w-24 animate-pulse rounded bg-muted" />
              ) : metrics ? (
                formatMetric(metrics, key)
              ) : (
                NOT_MEASURED
              )}
            </div>
            {unmeasured && !loading && (
              <div className="mt-1 text-xs text-muted-foreground">Not reported for this scope</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
